import { config } from '../config';
import type { MatchState, ObservationKind } from '../types';
import { revertIncrement, type CountState } from './counts';
import { isInMatch } from './machine';
import { ok, reject, type DomainResult } from './result';

/**
 * LIVE-6 — undo, as a predicate over committed data plus a target selection.
 *
 * **No undo table and no timer as truth** (design §5.4). The window is derived from
 * `lastIncrementTs`, a committed column, so it survives a process kill with nothing to reconstruct:
 * on resume the affordance is correctly open or closed because the data says so.
 *
 * LIVE-6 states four things that must never happen. Each has a mechanism here, and none of them is
 * "the caller passes the right rows":
 *
 * | Must not | Mechanism |
 * |---|---|
 * | delete another hypothesis's increment | the command carries only `hypothesisId`; the target is selected from candidates re-filtered on that id *inside* this function. The UI never supplies an observation id. |
 * | delete a `voiceNote` or `flag` | candidates are re-filtered to `kind === 'increment'` here, on top of the `[matchId+hypothesisId+kind+createdAt]` range that structurally cannot contain one (design §3.4) |
 * | act once the match leaves in-match states | `isInMatch` — Full Time closes every window instantly |
 * | exist as a global "undo last action" | no such function is exported, and `planUndoIncrement` cannot be called without naming a hypothesis |
 *
 * The re-filtering is deliberate belt-and-braces. The index range already excludes both classes of
 * row, but "the query happened to be right" is not the same claim as "the wrong row cannot be
 * chosen", and LIVE-6 makes the second claim. Feeding this function a deliberately polluted
 * candidate list is how the negative is proven.
 */

const MODULE = 'undo';

/** The undo window in milliseconds. `UNDO_WINDOW_S` is the configured value (NFR-9). */
function windowMs(): number {
  return config.UNDO_WINDOW_S * 1000;
}

/**
 * Design §5.4 — is the `−1` affordance open for this hypothesis?
 *
 * `lastIncrementTs` is the `createdAt` of the newest **surviving** increment, rewritten by both the
 * Increment and the UndoIncrement handlers. Because the newest is the most recent,
 * `now − newest < UNDO_WINDOW_S` is exactly equivalent to *"at least one increment on this
 * hypothesis is still inside its own window"* — that equivalence is what makes the stateless form
 * correct, and it is why repeat-undo walks back rather than re-anchoring.
 *
 * **Bounded by time, not by state.** `isInMatch` includes `halftime`, so an increment recorded at
 * 44:58 is still undoable after the 45:00 Half tap, for its full window. It excludes `fullTime`,
 * so Full Time closes every window at once.
 *
 * A `lastIncrementTs` in the future (a clock stepping backwards) reads as open rather than closed:
 * the alternative silently removes a correction the user is entitled to.
 */
export function undoOpen(
  hypothesis: { readonly lastIncrementTs: number | null },
  state: MatchState,
  now: number,
): boolean {
  if (!isInMatch(state)) return false;
  const last = hypothesis.lastIncrementTs;
  if (last === null || !Number.isFinite(last) || !Number.isFinite(now)) return false;
  return now - last < windowMs();
}

/**
 * How long until the **earliest** open window on this surface closes, or `null` if none is open.
 *
 * ── Why a surface needs this at all ──────────────────────────────────────────
 *
 * `undoOpen` is a function of `now`, and a React surface only re-renders when something tells it to.
 * An increment causes a render, so the `−1` **appears** for free; nothing causes a render eight
 * seconds later, so without this the affordance would linger until the next tap — visible, tappable,
 * and rejected by the in-transaction re-check. LIVE-6 says the affordance appears *for*
 * `UNDO_WINDOW_S`, which is a claim about when it stops.
 *
 * ── Why this is not the polling NFR-5 forbids ───────────────────────────────
 *
 * NFR-5 bans work that recurs on a cadence whether or not there is anything to do. This returns
 * `null` whenever no window is open, so the surface holds **no timer at all** in the state the app
 * spends almost all of its time in — the same assertable property §12.1a settles on for the logger's
 * deferred flush: *"no timer is outstanding while the buffer is empty."* When a window is open the
 * wait is a single one-shot bounded by `UNDO_WINDOW_S`, not an interval.
 *
 * It is also not a *deadline* in §2.4's sense, which is why the timer-suspension argument that bans
 * `setTimeout` for nudges does not apply: if the phone sleeps through it, nothing is missed —
 * `undoOpen` is still evaluated from committed data on the next render, and the lifecycle triggers
 * that settle DISC-7 re-render the surface anyway. The timer makes the affordance disappear
 * *promptly*; correctness never depends on it firing (SES-4's "best-effort" shape).
 *
 * Returns `0` for a window that has just closed, so a caller scheduling on the value cannot compute
 * a negative delay.
 */
export function msUntilUndoCloses(
  hypotheses: readonly { readonly lastIncrementTs: number | null }[],
  state: MatchState,
  now: number,
): number | null {
  if (!isInMatch(state)) return null;

  let earliest: number | null = null;
  for (const hypothesis of hypotheses) {
    if (!undoOpen(hypothesis, state, now)) continue;
    // `undoOpen` has already established this is a finite number.
    const closesIn = Math.max(0, (hypothesis.lastIncrementTs ?? 0) + windowMs() - now);
    if (earliest === null || closesIn < earliest) earliest = closesIn;
  }
  return earliest;
}

/**
 * §5.6's **top-zone undo strip**: which single hypothesis the strip offers to undo right now.
 *
 * ── Why a surface needs a different affordance at all ───────────────────────
 *
 * §5.6's predicate table routes `undoOpen` to *"in-tile `−1` inset on the locked board; `−1` on the
 * row in Discovery; **undo strip in the top zone** on the Set Piece sheet and the halftime hub"*. The
 * first two surfaces render the hypothesis, so the affordance can sit on it. The Set Piece sheet
 * renders a penalty-box diagram and the halftime hub renders a list of activities — neither has the
 * row the `−1` would attach to, and `undoOpen` is nonetheless true there. That is exactly the class
 * of defect §5.6 exists to close: *"a state predicate that stays true while the surface that would
 * expose it does not exist."*
 *
 * ── The single target, and LIVE-6's negative ────────────────────────────────
 *
 * The strip offers **one named hypothesis**, the one whose increment is newest. It is not, and must
 * never become, a global "undo last action": LIVE-6 forbids one by name, and the mechanism is that
 * this returns the row so the caller can **display its text** and dispatch `UndoIncrement` against
 * *that* `hypothesisId`. §17.1's fall-through row is the behaviour that follows for free — undo the
 * newest, and the next-newest still-open window becomes the target; when the last window closes this
 * returns `null` and the strip disappears. There is no queue and nothing to remember, because this is
 * a pick over committed data re-evaluated on every render (§5.4).
 *
 * Generic over the row so the caller keeps whatever it needs to render (the hypothesis text); this
 * function only ever reads `id` and `lastIncrementTs`.
 *
 * Ties break on `id`, descending, so two increments committed in the same millisecond do not make the
 * strip's target depend on array order.
 */
export function pickUndoTarget<T extends { readonly id: string; readonly lastIncrementTs: number | null }>(
  rows: readonly T[],
  state: MatchState,
  now: number,
): T | null {
  let best: T | null = null;
  for (const row of rows) {
    if (!undoOpen(row, state, now)) continue;
    if (best === null || isMoreRecent(row, best)) best = row;
  }
  return best;
}

function isMoreRecent(
  candidate: { readonly id: string; readonly lastIncrementTs: number | null },
  best: { readonly id: string; readonly lastIncrementTs: number | null },
): boolean {
  const a = candidate.lastIncrementTs ?? 0;
  const b = best.lastIncrementTs ?? 0;
  if (a !== b) return a > b;
  return candidate.id > best.id;
}

/** An Observation row as the undo selector sees it. */
export interface UndoCandidate {
  readonly id: string;
  readonly hypothesisId: string | null;
  readonly kind: ObservationKind;
  readonly createdAt: number;
}

export interface UndoPlan {
  /** The single `increment` Observation to delete (INV-1's one permitted deletion). */
  readonly deleteObservationId: string;
  /** The hypothesis's new `{cachedCount, status, lastIncrementTs}`, written in the same transaction. */
  readonly next: CountState;
}

export interface UndoRequest {
  /** The only target the command carries. There is no observation id on the wire. */
  readonly hypothesisId: string;
  readonly state: MatchState;
  readonly now: number;
  /** The hypothesis's committed count state, read inside the transaction. */
  readonly current: CountState;
  /**
   * Rows from the `[matchId+hypothesisId+kind+createdAt]` range. Re-filtered here regardless of
   * what the caller passed — see the module note.
   */
  readonly candidates: readonly UndoCandidate[];
}

/**
 * LIVE-6 — plan one undo: which increment to delete, and what the hypothesis becomes.
 *
 * Repeat activation is this same call again, against the updated state. Design §5.4's worked case:
 * taps at t=0,2,4,6 s; undo at 7 deletes t=6; at 7.5 deletes t=4; at 8 deletes t=2; at 8.5 the
 * predicate is `8.5 − 0 = 8.5 > 8` and the window is **closed**, so the t=0 increment is correctly
 * no longer undoable.
 *
 * The window is re-checked here, inside what the caller runs in its transaction; the UI's check is
 * only a render hint (design §5.4).
 */
export function planUndoIncrement(request: UndoRequest): DomainResult<UndoPlan> {
  const { hypothesisId, state, now, current, candidates } = request;

  if (!isInMatch(state)) {
    return reject(
      MODULE,
      null,
      'ERR_UNDO_NOT_IN_MATCH',
      'Undo is only available while the match is underway.',
      { hypothesisId, state },
    );
  }

  if (!undoOpen(current, state, now)) {
    return reject(
      MODULE,
      null,
      'ERR_UNDO_WINDOW_CLOSED',
      `The ${config.UNDO_WINDOW_S}-second undo window for this hypothesis has closed.`,
      { hypothesisId, state, lastIncrementTs: current.lastIncrementTs, now },
    );
  }

  // The two negatives LIVE-6 names, enforced on the data rather than trusted from the query.
  const own = candidates.filter(
    (candidate) => candidate.kind === 'increment' && candidate.hypothesisId === hypothesisId,
  );

  if (own.length === 0) {
    return reject(
      MODULE,
      null,
      'ERR_UNDO_NO_TARGET',
      'There is no increment left to undo on this hypothesis.',
      { hypothesisId, candidatesConsidered: candidates.length },
    );
  }

  const newest = own.reduce((best, candidate) => (isNewer(candidate, best) ? candidate : best));
  const surviving = own.filter((candidate) => candidate.id !== newest.id);
  const newLastIncrementTs =
    surviving.length === 0 ? null : Math.max(...surviving.map((candidate) => candidate.createdAt));

  return ok({
    deleteObservationId: newest.id,
    next: revertIncrement(current, newLastIncrementTs),
  });
}

/** Newest wins; identical timestamps fall through to the id so the choice is deterministic. */
function isNewer(candidate: UndoCandidate, best: UndoCandidate): boolean {
  if (candidate.createdAt !== best.createdAt) return candidate.createdAt > best.createdAt;
  return candidate.id > best.id;
}
